import { useMutation } from "@apollo/client";
import * as React from "react";
import { useState } from "react";
import { TicketDatabase } from "../Utils/TicketDatabase";
import { ExtendTicket, TicketExtended } from "../Utils/TypeExtensions";
import { Ticket } from "../sharedCode/avroGraphql";
import { CREATE_OR_UPDATE_TICKET } from "../sharedCode/queries";
import { logSentry } from "../Utils/Sentry";
import TicketValidator, { FAILED_STALE } from "../Utils/TicketValidator";
import { BackgroundProps, ErrorLevel, SyncStatus, TableSyncer } from "../Utils/Interfaces";
import dayjs from "dayjs";
import { DATE_FORMAT } from "../sharedCode/metadata";
import { isLastRevision } from "../sharedCode/HelperMethods";

const PAYLOAD_CHECKSUM_HEADER = "x-ticket-payload-sha256";

async function sha256Hex(value: string): Promise<string> {
    const data = new TextEncoder().encode(value);
    const hash = await crypto.subtle.digest("SHA-256", data);
    return Array.from(new Uint8Array(hash))
        .map((b) => b.toString(16).padStart(2, "0"))
        .join("");
}

function localPropertyRemover(key: string, value: any) {
    // don't send any of the extended properties
    const fieldsToRemove = [
        "hasLocalChanges",
        "nextLocalId",
        "Company",
        "Pilot",
        "Pilot2",
        "AME",
        "Aircraft",
        "isDemoTicket",
        "keyHash",
        "valueHash",
        "selectName",
        "partialSyncSnapshot",
    ];
    if (fieldsToRemove.findIndex((unwantedFieldName) => unwantedFieldName === key) >= 0) {
        return undefined;
    }

    // set any negative id fields to null since they're local ID's and will cause an error if we send them
    if (key === "id" && typeof value === "number" && value < 0) {
        return undefined;
    }

    return value;
}

/** Local-only fields for a successful push response; clear partialSyncSnapshot now that sync matched. */
function preserveLocalTicketFieldsOnSuccess(
    localTicket: TicketExtended
): Pick<TicketExtended, "hasLocalChanges" | "nextLocalId" | "OpenedCards" | "pushAttempts" | "isDemoTicket" | "isDuplicateTicket" | "partialSyncSnapshot"> {
    return {
        hasLocalChanges: false,
        nextLocalId: localTicket.nextLocalId,
        OpenedCards: localTicket.OpenedCards ?? [],
        pushAttempts: 0,
        isDemoTicket: localTicket.isDemoTicket ?? false,
        isDuplicateTicket: localTicket.isDuplicateTicket ?? false,
        partialSyncSnapshot: undefined,
    };
}

/** Local-only fields to preserve on failure (server never returns these; keep pushAttempts from server stub). */
function preserveLocalTicketFieldsOnFailure(
    localTicket: TicketExtended
): Pick<TicketExtended, "nextLocalId" | "OpenedCards" | "isDemoTicket" | "isDuplicateTicket" | "partialSyncSnapshot"> {
    return {
        nextLocalId: localTicket.nextLocalId,
        OpenedCards: localTicket.OpenedCards ?? [],
        isDemoTicket: localTicket.isDemoTicket ?? false,
        isDuplicateTicket: localTicket.isDuplicateTicket ?? false,
        partialSyncSnapshot: localTicket.partialSyncSnapshot,
    };
}

export default function useStaleTicketBackgroundPush(props: BackgroundProps): TableSyncer {
    const [status, setStatus] = useState(SyncStatus.Fresh);
    const [pushTicket] = useMutation(CREATE_OR_UPDATE_TICKET, { fetchPolicy: "no-cache" });

    // TODO controlling the useEffect with this const causes it to run once and kick off the pollForChanges loop... there's probably a more elegant way to do this
    const useEffectAnchor = "TODO";

    function pushTicketsToServer(ticketsToUpdate: Array<TicketExtended>) {
        return ticketsToUpdate.map(async (unsyncedTicket) => {
            try {
                const jsonToPush = JSON.stringify({ ticket: unsyncedTicket }, localPropertyRemover);
                const payloadChecksum = await sha256Hex(jsonToPush);

                // throw new ApolloError({ errorMessage: "push failed" });
                const serverResponse = await pushTicket({
                    variables: {
                        ticket: jsonToPush,
                    },
                    context: {
                        headers: {
                            [PAYLOAD_CHECKSUM_HEADER]: payloadChecksum,
                        },
                    },
                });
                return {
                    localTicket: unsyncedTicket,
                    serverTicket: ExtendTicket(serverResponse.data.createOrUpdateTicket as Ticket),
                    success: true,
                };
            } catch (e) {
                logSentry(
                    "FAILED_STALE_PUSH",
                    `${unsyncedTicket.ticket_num} ${dayjs(unsyncedTicket.date).format(DATE_FORMAT)} c:${unsyncedTicket.company_id} j:${unsyncedTicket.job_id} e:${
                        (e as Error).message ?? ""
                    }`
                );

                return {
                    localTicket: unsyncedTicket,
                    serverTicket: {
                        ...unsyncedTicket,
                        pushAttempts: FAILED_STALE,
                    } as TicketExtended,
                    success: false,
                };
            }
        });
    }

    function saveResponsesToLocalDb(
        serverResponses: {
            success: boolean;
            localTicket: TicketExtended;
            serverTicket: TicketExtended;
        }[]
    ) {
        return serverResponses.map(async (pushResult) => {
            try {
                return await TicketDatabase.transaction("rw", TicketDatabase.ticket, async () => {
                    // push was completely rejected, just save the ticket with its new status and bail out (preserve local-only fields)
                    if (!pushResult.success) {
                        await TicketDatabase.ticket.put({ ...pushResult.serverTicket, ...preserveLocalTicketFieldsOnFailure(pushResult.localTicket) }, pushResult.localTicket.id);
                        return { ticketNumber: pushResult.localTicket.ticket_num, success: false };
                    }

                    const toSave = {
                        ...pushResult.serverTicket,
                        ...preserveLocalTicketFieldsOnSuccess(pushResult.localTicket),
                    };
                    if (pushResult.serverTicket.id !== pushResult.localTicket.id) {
                        await TicketDatabase.ticket.delete(pushResult.localTicket.id);
                        await TicketDatabase.ticket.put(toSave);
                    } else {
                        const updateCount = await TicketDatabase.ticket.update(pushResult.localTicket.id, toSave);
                        if (updateCount !== 1) {
                            return { ticketNumber: pushResult.localTicket.ticket_num, success: false };
                        }
                    }
                    return { ticketNumber: pushResult.localTicket.ticket_num, success: true };
                });
            } catch (e: any) {
                logSentry("FAILED_STALE_LOCAL_SAVE", `${pushResult.localTicket.ticket_num} | ${(e as Error).message ?? ""}`);
                return { ticketNumber: pushResult.localTicket.ticket_num, success: false };
            }
        });
    }

    const fetchData = async () => {
        setStatus(SyncStatus.Loading);

        const aircraftList = await TicketDatabase.aircraft.toArray();
        const freshTicketList = await TicketDatabase.ticket.toArray();
        const ticketsToUpdate = await TicketDatabase.ticket
            .filter(
                (ticket) =>
                    (ticket.isDemoTicket === undefined ? false : ticket.isDemoTicket) === false &&
                    ticket.id < 0 &&
                    TicketValidator.IsStale(ticket) &&
                    ticket.pushAttempts !== FAILED_STALE &&
                    ticket.hasLocalChanges &&
                    TicketValidator.IsReadOnly(ticket) &&
                    isLastRevision(freshTicketList, ticket) &&
                    TicketValidator.validate(aircraftList, freshTicketList, ticket).filter((vr) => vr.level === ErrorLevel.Error).length === 0
            )
            .toArray();

        const serverResponses = await Promise.all(pushTicketsToServer(ticketsToUpdate));

        const localResponses = await Promise.all(saveResponsesToLocalDb(serverResponses));

        // if any syncs failed status will already be set to stale
        setStatus((prev) => {
            return prev === SyncStatus.Loading ? SyncStatus.Fresh : prev;
        });
    };

    return { status, fetchData: fetchData };
}
